import { lazyPageFactories } from './lazyPages.js';

// Idle-Preload für die top-Navigation-Targets nach Login. Triggert dynamic
// `import()` der häufigsten Lazy-Pages während der Browser idle ist —
// gleiche Modul-Identität wie React.lazy benutzt (gemeinsame Factory), daher
// kein zweiter Fetch beim späteren Klick. Module-Graph cached den Promise.
//
// Pattern siehe memory/preload-patterns.md:
// - requestIdleCallback mit Timeout-Fallback (NICHT naked setTimeout)
// - Trigger NACH auth-authed (nicht im checking/unauthed-Branch)
// - Scope probabilistisch: AssetDetailPage (P(Library→AssetDetail) hoch wenn
//   Library eager ist) + CollectionListPage (Hauptnav-Section).
//   Bewusst NICHT preloaden: Upload (größter chunk, bewusst seltener),
//   Login (only unauthed branch).

const IDLE_TIMEOUT_MS = 3000;
const FALLBACK_DELAY_MS = 2000;

type CleanupFn = () => void;

// Pages die nach Login wahrscheinlich als nächstes besucht werden.
// Reihenfolge = preload-Priorität (factory wird seriell getriggert, Module-Graph
// arbeitet aber parallele Promise-Resolution darunter).
// Sprint B PR-B1: CollectionListPage als preload-target (Hauptnav-Section,
// häufige Frequenz erwartet — analog AssetDetail).
const PRELOAD_TARGETS = [
  'AssetDetailPage',
  'CollectionListPage',
] as const;

export function schedulePreloads(): CleanupFn {
  let cancelled = false;

  const runPreloads = (): void => {
    if (cancelled) return;
    for (const name of PRELOAD_TARGETS) {
      // .catch silent: preload-Fehler dürfen niemals UI-Feedback erzeugen.
      // Wenn der echte Klick später kommt, fängt LazyChunkErrorBoundary den
      // dann erneut auftretenden Fehler über lazyWithRetry's auto-retry-Pfad.
      lazyPageFactories[name]().catch(() => {});
    }
  };

  if (typeof requestIdleCallback === 'function') {
    const handle = requestIdleCallback(runPreloads, { timeout: IDLE_TIMEOUT_MS });
    return () => {
      cancelled = true;
      if (typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(handle);
      }
    };
  }

  // Safari < 17 fallback — setTimeout idle-approximation
  const handle = setTimeout(runPreloads, FALLBACK_DELAY_MS);
  return () => {
    cancelled = true;
    clearTimeout(handle);
  };
}
